// src/pages/OrganizationSetupPage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
    Box, Button, TextField, Typography, Container, Paper, Alert,
    CircularProgress, IconButton, Grid, InputAdornment,
    Stack, Tooltip, Tabs, Tab, Divider,
} from '@mui/material';
import { Add, Delete, Business, Close, CloudUpload } from '@mui/icons-material';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { IMaskInput } from 'react-imask';
import PropTypes from 'prop-types';
import myLogo from '../assets/sua-logo.png';
import useAuth from '../hooks/useAuth';

const lightTheme = createTheme({
    palette: { primary: { main: '#ff834f' } },
    shape: { borderRadius: 12 },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
    },
}));

// Função para validar o formato do CNPJ (sem alterações)
function validateCnpj(cnpj) {
    const cleanedCnpj = cnpj.replace(/[^\d]/g, '');
    if (cleanedCnpj.length !== 14 || /^(\d)\1+$/.test(cleanedCnpj)) return false;
    let sum = 0, weights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 12; i++) { sum += parseInt(cleanedCnpj.charAt(i)) * weights[i]; }
    let remainder = sum % 11;
    const firstDigit = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cleanedCnpj.charAt(12)) !== firstDigit) return false;
    sum = 0; weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 13; i++) { sum += parseInt(cleanedCnpj.charAt(i)) * weights[i]; }
    remainder = sum % 11;
    const secondDigit = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cleanedCnpj.charAt(13)) !== secondDigit) return false;
    return true;
}

const CnpjMaskCustom = React.forwardRef((props, ref) => <IMaskInput {...props} mask="00.000.000/0000-00" inputRef={ref} onAccept={(value) => props.onChange({ target: { name: props.name, value } })} overwrite />);
const CepMaskCustom = React.forwardRef((props, ref) => <IMaskInput {...props} mask="00000-000" inputRef={ref} onAccept={(value) => props.onChange({ target: { name: props.name, value } })} overwrite />);
CnpjMaskCustom.propTypes = { name: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };
CepMaskCustom.propTypes = { name: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const unitLimits = {
    pequena: 2,
    media: 3,
    grande: 6,
};

// ============================================================================
// COMPONENTE UnitForm - ATUALIZADO COM VALIDAÇÃO DE CNPJ
// ============================================================================
const UnitForm = ({ unit, index, onUnitChange }) => {
    const [cnpjValidationError, setCnpjValidationError] = useState('');
    const [cnpjChecking, setCnpjChecking] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);
    const [cepError, setCepError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            if (files && files[0]) onUnitChange(index, { logo: files[0], logoPreview: URL.createObjectURL(files[0]) });
        } else {
            onUnitChange(index, { [name]: value });
        }
        if (name === 'cnpj') setCnpjValidationError(''); // Limpa o erro ao digitar
    };

    // Função que verifica o formato e a unicidade do CNPJ
    const handleCnpjBlur = async (e) => {
        const cnpj = e.target.value;
        const cleanedCnpj = cnpj.replace(/[^\d]/g, '');
        
        if (cleanedCnpj.length !== 14) {
            setCnpjValidationError('');
            return;
        }
        if (!validateCnpj(cnpj)) {
            setCnpjValidationError('Formato de CNPJ inválido.');
            return;
        }
        
        setCnpjChecking(true);
        setCnpjValidationError('');
        try {
            await api.get(`/organization/units/check-cnpj?cnpj=${cleanedCnpj}`);
        } catch (error) {
            console.error("Erro detalhado ao verificar CNPJ:", error.response || error);

            if (error.response && error.response.status === 409) {
                setCnpjValidationError('Este CNPJ já está em uso.');
            } else {
                setCnpjValidationError('Erro ao verificar CNPJ.');
            }
        } finally {
            setCnpjChecking(false);
        }
    };

    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setCepLoading(true); setCepError('');
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();
            if (data.erro) throw new Error('CEP não encontrado.');
            onUnitChange(index, { cep: data.cep, address: data.logradouro, city: data.localidade, state: data.uf });
        } catch (error) {
            setCepError(error.message);
        } finally {
            setCepLoading(false);
        }
    };

    return (
        <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <Grid container spacing={2.5}>
                <Grid item xs={12}><StyledTextField name="name" label="Nome da Empresa/Unidade" fullWidth required value={unit.name || ''} onChange={handleChange} /></Grid>
                
                <Grid item xs={12} sm={7}>
                    <StyledTextField 
                        name="cnpj" 
                        label="CNPJ" 
                        fullWidth 
                        required 
                        value={unit.cnpj || ''} 
                        onChange={handleChange} 
                        onBlur={handleCnpjBlur} 
                        error={!!cnpjValidationError} 
                        helperText={cnpjValidationError} 
                        InputProps={{ 
                            inputComponent: CnpjMaskCustom,
                            endAdornment: cnpjChecking && <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                        }} 
                    />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                    <StyledTextField name="cep" label="CEP" fullWidth required value={unit.cep || ''} onChange={handleChange} onBlur={handleCepBlur} error={!!cepError} helperText={cepError || "Preencha para buscar o endereço"} InputProps={{ inputComponent: CepMaskCustom, endAdornment: cepLoading && <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}><StyledTextField name="address" label="Endereço" fullWidth required value={unit.address || ''} onChange={handleChange} disabled={cepLoading} InputLabelProps={{ shrink: !!unit.address }}/></Grid>
                <Grid item xs={12} sm={8}><StyledTextField name="city" label="Cidade" fullWidth required value={unit.city || ''} onChange={handleChange} disabled={cepLoading} InputLabelProps={{ shrink: !!unit.city }}/></Grid>
                <Grid item xs={12} sm={4}><StyledTextField name="state" label="Estado" fullWidth required value={unit.state || ''} onChange={handleChange} disabled={cepLoading} InputLabelProps={{ shrink: !!unit.state }}/></Grid>
                <Grid item xs={12}>
                     <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>Logo da Unidade (Opcional)</Typography>
                     {/* --- INÍCIO DA ALTERAÇÃO --- */}
                     <Box 
                         component="label" 
                         htmlFor={`logo-upload-${index}`} 
                         sx={{ 
                             display: 'flex',
                             flexDirection: 'column',
                             alignItems: 'center',
                             justifyContent: 'center',
                             border: '2px dashed', 
                             borderColor: 'divider', 
                             borderRadius: 2, 
                             p: 3, 
                             minHeight: '160px',
                             textAlign: 'center', 
                             cursor: 'pointer', 
                             transition: 'border-color 0.2s, background-color 0.2s',
                             '&:hover': { 
                                 borderColor: 'primary.main',
                                 bgcolor: 'action.hover'
                             } 
                         }}
                     >
                         <input id={`logo-upload-${index}`} name="logo" type="file" accept="image/*" hidden onChange={handleChange} />
                         {unit.logoPreview ? (
                             <img src={unit.logoPreview} alt="Preview" style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }} />
                         ) : (
                             <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                                 <CloudUpload sx={{ fontSize: 40 }} />
                                 <Typography>Clique para enviar</Typography>
                             </Stack>
                         )}
                     </Box>
                     {/* --- FIM DA ALTERAÇÃO --- */}
                </Grid>
            </Grid>
        </motion.div>
    );
};

const OrganizationSetupPage = () => {
    const [units, setUnits] = useState([{ name: 'Matriz', isMatriz: true }]);
    const [selectedUnitIndex, setSelectedUnitIndex] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const [unitLimitError, setUnitLimitError] = useState('');

    useEffect(() => {
        if (user === undefined) return;
       if (user && (user.companyId || user.isSingular)) {
            navigate('/inicial');
            return;
        }
        if (user && user.role === 'admin' && !user.licenseType) {
            setError('Seu usuário não possui uma licença. Contate o superadmin.');
        } else {
            setError('');
        }
        setLoading(false);
    }, [user, navigate]);

    const getUnitLimit = useCallback(() => user ? unitLimits[user.licenseType?.toLowerCase()] : undefined, [user]);

    const handleTabChange = (event, newValue) => setSelectedUnitIndex(newValue);

    const handleAddUnit = () => {
        const limit = getUnitLimit();
        if (limit !== undefined && units.length >= limit) {
            setUnitLimitError(`Limite de ${limit} unidades atingido para a licença ${user.licenseType}.`);
            return;
        }
        setUnits([...units, { name: `Filial ${units.length}`, isMatriz: false }]);
        setSelectedUnitIndex(units.length);
        setUnitLimitError('');
    };

    const handleRemoveUnit = (e, indexToRemove) => {
        e.stopPropagation();
        setUnits(units.filter((_, i) => i !== indexToRemove));
        setSelectedUnitIndex(prev => Math.max(0, prev - 1));
        setUnitLimitError('');
    };

    const handleUnitChange = useCallback((index, changedFields) => {
        setUnits(prev => prev.map((unit, i) => i === index ? { ...unit, ...changedFields } : unit));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        const cnpjs = units.map(u => u.cnpj?.replace(/[^\d]/g, '')).filter(Boolean);
        if (new Set(cnpjs).size !== cnpjs.length) {
            setError('Existem CNPJs duplicados na sua lista de unidades. Cada unidade deve ter um CNPJ único.');
            setLoading(false);
            return;
        }

        const formData = new FormData();
        units.forEach((unit, index) => {
            formData.append(`units[${index}][name]`, unit.name || '');
            formData.append(`units[${index}][isMatriz]`, !!unit.isMatriz);
            formData.append(`units[${index}][cnpj]`, unit.cnpj || '');
            formData.append(`units[${index}][cep]`, unit.cep || '');
            formData.append(`units[${index}][address]`, unit.address || '');
            formData.append(`units[${index}][city]`, unit.city || '');
            formData.append(`units[${index}][state]`, unit.state || '');
            if (unit.logo) formData.append(`units[${index}][logo]`, unit.logo);
        });

        try {
            const response = await api.post('/organization/setup', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            login(response.data.token);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível criar a organização.');
        } finally {
            setLoading(false);
        }
    };

    const currentUnitLimit = getUnitLimit();
    const isAddUnitDisabled = currentUnitLimit !== undefined && units.length >= currentUnitLimit;
    const isSubmitDisabled = loading || !user || !user.licenseType || !!unitLimitError;

    return (
        <ThemeProvider theme={lightTheme}>
            <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
                <Container component="main" maxWidth="md">
                    <Paper component="form" onSubmit={handleSubmit} variant="outlined" sx={{ borderRadius: 2.5 }}>
                        <Box sx={{ textAlign: 'center', p: 3, borderBottom: 1, borderColor: 'divider' }}>
                            <img src={myLogo} alt="Logo" style={{ width: '140px', marginBottom: '8px' }} />
                            <Typography variant="h5" fontWeight={700}>Configuração da Organização</Typography>
                            <Typography color="text.secondary" variant="body2">Adicione e configure as unidades da sua empresa.</Typography>
                        </Box>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', pl: {xs: 1, sm: 2} }}>
                            <Tabs value={selectedUnitIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1 }}>
                                {units.map((unit, index) => (
                                    <Tab key={index} label={
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Business sx={{ fontSize: 18 }} />
                                            <Typography variant="body2" fontWeight={600}>{unit.name || `Unidade ${index + 1}`}</Typography>
                                            {index > 0 && (<IconButton size="small" component="span" onClick={(e) => handleRemoveUnit(e, index)}><Close sx={{ fontSize: 16 }} /></IconButton>)}
                                        </Stack>
                                    } sx={{ textTransform: 'none' }} />
                                ))}
                            </Tabs>
                            <Tooltip title="Adicionar Nova Unidade"><Button onClick={handleAddUnit} sx={{ ml: 1, mr: 1 }} disabled={isAddUnitDisabled}><Add /></Button></Tooltip>
                        </Box>
                        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                            {user?.licenseType && (<Alert severity="info" sx={{ mb: 2 }}>Sua licença é do tipo:  <strong>{user.licenseType.toUpperCase()}</strong>. Você pode cadastrar até <strong>{currentUnitLimit} unidades</strong> (incluindo a matriz). Atualmente: <strong>{units.length}</strong>.</Alert>)}
                            {unitLimitError && (<Alert severity="error" sx={{ mb: 2 }}>{unitLimitError}</Alert>)}
                            <AnimatePresence mode="wait"><UnitForm key={selectedUnitIndex} unit={units[selectedUnitIndex]} index={selectedUnitIndex} onUnitChange={handleUnitChange} /></AnimatePresence>
                        </Box>
                        <Divider />
                        <Box sx={{ p: { xs: 2, sm: 3 }}}>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitDisabled} sx={{ py: 1.5 }}>
                                {loading ? <CircularProgress size={26} color="inherit" /> : 'Finalizar e Acessar Plataforma'}
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default OrganizationSetupPage;
